/**
 * Универсальный движок запросов для CSElement
 * Поддерживает CSS-селекторы, XPath, объектные селекторы, индексирование и оптимизацию запросов
 */

import { ICSElement, QuerySelector } from '../types/interfaces';
import * as JSPath from 'jspath';
import { CSElement } from '../core/CSElement';

/**
 * Типы селекторов
 */
export enum SelectorType {
  CSS = 'css',
  XPATH = 'xpath',
  OBJECT = 'object'
}

/**
 * CSS-селектор
 */
export interface CSSSelector {
  type: SelectorType.CSS;
  selector: string;
}

/**
 * XPath-селектор
 */
export interface XPathSelector {
  type: SelectorType.XPATH;
  expression: string;
}

/**
 * Объектный селектор
 */
export interface ObjectSelector {
  type: SelectorType.OBJECT;
  selector: QuerySelector;
}

/**
 * Универсальный селектор
 */
export type UniversalSelector = CSSSelector | XPathSelector | ObjectSelector | string;

/**
 * Результат парсинга CSS-селектора
 */
interface ParsedCSSSelector {
  element?: string;
  id?: string;
  classes?: string[];
  attributes?: { name: string; operator?: string; value?: string }[];
  pseudoClasses?: { name: string; argument?: string }[];
  combinator?: '>' | '+' | '~' | ' ';
  next?: ParsedCSSSelector;
}

/**
 * Индекс для быстрого поиска
 */
interface ElementIndex {
  byName: Map<string, Set<ICSElement>>;
  byId: Map<string, ICSElement>;
  byClass: Map<string, Set<ICSElement>>;
  byAttribute: Map<string, Map<any, Set<ICSElement>>>;
  byDepth: Map<number, Set<ICSElement>>;
  all: Set<ICSElement>;
}

/**
 * Статистика запросов
 */
export interface QueryStats {
  totalQueries: number;
  cacheHits: number;
  averageExecutionTime: number;
  slowestQuery: {
    selector: string;
    time: number;
  };
  mostFrequentSelectors: Array<{
    selector: string;
    count: number;
  }>;
}

/**
 * Расширенный движок запросов
 */
export class QueryEngine {
  private static indices = new WeakMap<ICSElement, ElementIndex>();
  private static queryCache = new Map<string, { result: ICSElement[]; timestamp: number }>();
  private static cacheTimeout = 5000; // 5 секунд
  private static stats: QueryStats = {
    totalQueries: 0,
    cacheHits: 0,
    averageExecutionTime: 0,
    slowestQuery: { selector: '', time: 0 },
    mostFrequentSelectors: []
  };
  private static selectorFrequency = new Map<string, number>();

  /**
   * Выполняет поиск элементов по универсальному селектору
   */
  static query(root: ICSElement, selector: UniversalSelector): ICSElement[] {
    const startTime = performance.now();
    const selectorString = this.getSelectorString(selector);
    
    // Создаем ключ кэша, который включает ID корневого элемента
    const cacheKey = `${root.id}:${selectorString}`;
    
    // Проверяем кэш
    const cached = this.getCachedResult(cacheKey);
    if (cached) {
      this.stats.cacheHits++;
      this.stats.totalQueries++; // Кэшированные запросы тоже считаются как запросы
      
      // Обновляем частоту селекторов для кэшированных запросов
      const currentCount = this.selectorFrequency.get(selectorString) || 0;
      this.selectorFrequency.set(selectorString, currentCount + 1);
      
      // Обновляем топ селекторов
      this.stats.mostFrequentSelectors = Array.from(this.selectorFrequency.entries())
        .map(([sel, count]) => ({ selector: sel, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
      
      return cached;
    }

    let result: ICSElement[];

    if (typeof selector === 'string') {
      // Автоматически определяем тип селектора
      if (selector.startsWith('//') || selector.startsWith('/')) {
        result = this.queryXPath(root, selector);
      } else {
        result = this.queryCSS(root, selector);
      }
    } else {
      switch (selector.type) {
        case SelectorType.CSS:
          result = this.queryCSS(root, selector.selector);
          break;
        case SelectorType.XPATH:
          result = this.queryXPath(root, selector.expression);
          break;
        case SelectorType.OBJECT:
          result = this.queryObject(root, selector.selector);
          break;
        default:
          throw new Error(`Неподдерживаемый тип селектора: ${(selector as any).type}`);
      }
    }

    // Обновляем статистику
    const executionTime = performance.now() - startTime;
    this.updateStats(selectorString, executionTime);

    // Кэшируем результат с учетом корневого элемента
    this.cacheResult(cacheKey, result);

    return result;
  }

  /**
   * Находит первый элемент по селектору
   */
  static queryOne(root: ICSElement, selector: UniversalSelector): ICSElement | null {
    const results = this.query(root, selector);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Выполняет поиск по CSS-селектору
   */
  static queryCSS(root: ICSElement, selector: string): ICSElement[] {
    const parsed = this.parseCSS(selector);
    return this.executeCSS(root, parsed);
  }

  /**
   * Выполняет поиск по XPath
   */
  static queryXPath(root: ICSElement, expression: string): ICSElement[] {
    const context = this.createXPathContext(root);
    return this.evaluateXPath(context, expression);
  }

  /**
   * Выполняет поиск по объектному селектору
   */
  static queryObject(root: ICSElement, selector: QuerySelector): ICSElement[] {
    return this.queryBySelector(root, selector);
  }

  /**
   * Включить/выключить индексирование
   */
  static setIndexingEnabled(enabled: boolean): void {
    // В данной реализации индексирование всегда включено
    // Этот метод оставлен для совместимости API
    console.log(`Индексирование ${enabled ? 'включено' : 'выключено'}`);
  }

  /**
   * Создает или обновляет индекс для элемента
   */
  static buildIndex(root: ICSElement): void {
    const index: ElementIndex = {
      byName: new Map(),
      byId: new Map(),
      byClass: new Map(),
      byAttribute: new Map(),
      byDepth: new Map(),
      all: new Set()
    };

    this.traverseAndIndex(root, index, 0);
    this.indices.set(root, index);
  }

  /**
   * Получает индекс для элемента
   */
  static getIndex(root: ICSElement): ElementIndex {
    let index = this.indices.get(root);
    if (!index) {
      this.buildIndex(root);
      index = this.indices.get(root)!;
    }
    return index;
  }

  /**
   * Очищает кэш запросов
   */
  static clearCache(): void {
    this.queryCache.clear();
  }

  /**
   * Получает статистику запросов
   */
  static getStats(): QueryStats {
    return { ...this.stats };
  }

  /**
   * Сбрасывает статистику
   */
  static resetStats(): void {
    this.stats = {
      totalQueries: 0,
      cacheHits: 0,
      averageExecutionTime: 0,
      slowestQuery: { selector: '', time: 0 },
      mostFrequentSelectors: []
    };
    this.selectorFrequency.clear();
  }

  /**
   * Приватные методы
   */

  private static getSelectorString(selector: UniversalSelector): string {
    if (typeof selector === 'string') {
      return selector;
    }
    switch (selector.type) {
      case SelectorType.CSS:
        return `css:${selector.selector}`;
      case SelectorType.XPATH:
        return `xpath:${selector.expression}`;
      case SelectorType.OBJECT:
        return `object:${JSON.stringify(selector.selector)}`;
      default:
        return 'unknown';
    }
  }

  private static getCachedResult(selector: string): ICSElement[] | null {
    const cached = this.queryCache.get(selector);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.result;
    }
    if (cached) {
      this.queryCache.delete(selector);
    }
    return null;
  }

  private static cacheResult(selector: string, result: ICSElement[]): void {
    this.queryCache.set(selector, {
      result: [...result],
      timestamp: Date.now()
    });
  }

  private static updateStats(selector: string, executionTime: number): void {
    this.stats.totalQueries++;
    
    // Обновляем среднее время выполнения
    this.stats.averageExecutionTime = 
      (this.stats.averageExecutionTime * (this.stats.totalQueries - 1) + executionTime) / this.stats.totalQueries;
    
    // Обновляем самый медленный запрос
    if (executionTime > this.stats.slowestQuery.time) {
      this.stats.slowestQuery = { selector, time: executionTime };
    }
    
    // Обновляем частоту селекторов
    const currentCount = this.selectorFrequency.get(selector) || 0;
    this.selectorFrequency.set(selector, currentCount + 1);
    
    // Обновляем топ селекторов
    this.stats.mostFrequentSelectors = Array.from(this.selectorFrequency.entries())
      .map(([sel, count]) => ({ selector: sel, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  private static parseCSS(selector: string): ParsedCSSSelector {
    // Улучшенный CSS парсер
    const result: ParsedCSSSelector = {};
    

    
    // Убираем лишние пробелы и разбиваем по комбинаторам
    const trimmed = selector.trim();
    // Измененный regex, чтобы поддерживать селекторы, начинающиеся с комбинатора
    const combinatorMatch = trimmed.match(/^([>+~])?\s*(.*)$/);
    
    if (!combinatorMatch) {
      this.parseSelectorPart(trimmed, result);
      return result;
    }

    const [, combinator, rest] = combinatorMatch;
    
    if (combinator) {
      result.combinator = combinator as any;
      const nextParts = rest.split(/\s*([>+~])\s*/);
      result.next = this.parseCSS(nextParts.join(' '));
      return result;
    }

    // Старая логика для селекторов, не начинающихся с комбинатора
    const simpleMatch = trimmed.match(/^([^>+~\s]+)(?:\s*([>+~]|\s+)\s*(.+))?$/);
    if (!simpleMatch) {
      this.parseSelectorPart(trimmed, result);
      return result;
    }
    
    const [, currentPart, simpleCombinator, simpleRest] = simpleMatch;
    this.parseSelectorPart(currentPart, result);

    if (simpleCombinator && simpleRest) {
      result.combinator = simpleCombinator.trim() === '' ? ' ' : simpleCombinator.trim() as any;
      result.next = this.parseCSS(simpleRest);
    }
    

    
    return result;
  }

  private static parseSelectorPart(part: string, result: ParsedCSSSelector): void {
    let remaining = part;

    // Парсим элемент, ID, классы, атрибуты и псевдо-классы
    while (remaining) {
      if (remaining.startsWith('#')) {
        // ID селектор
        const match = remaining.match(/^#([^.:\[]+)/);
        if (match) {
          result.id = match[1];
          remaining = remaining.substring(match[0].length);
        } else {
          break;
        }
      } else if (remaining.startsWith('.')) {
        // Class селектор
        const match = remaining.match(/^\.([^.:\[#]+)/);
        if (match) {
          if (!result.classes) result.classes = [];
          result.classes.push(match[1]);
          remaining = remaining.substring(match[0].length);
        } else {
          break;
        }
      } else if (remaining.startsWith('[')) {
        // Атрибутный селектор
        const match = remaining.match(/^\[([^=\]]+)(?:(=|~=|\|=|\^=|\$=|\*=)"?([^"\]]+)"?)?\]/);
        if (match) {
          if (!result.attributes) result.attributes = [];
          result.attributes.push({
            name: match[1],
            operator: match[2],
            value: match[3]
          });
          remaining = remaining.substring(match[0].length);
        } else {
          break;
        }
      } else if (remaining.startsWith(':')) {
        // Псевдо-класс
        const match = remaining.match(/^:([^:(]+)(?:\(([^)]+)\))?/);
        if (match) {
          if (!result.pseudoClasses) result.pseudoClasses = [];
          result.pseudoClasses.push({
            name: match[1],
            argument: match[2]
          });
          remaining = remaining.substring(match[0].length);
        } else {
          break;
        }
      } else {
        // Имя элемента (должно быть в начале)
        const match = remaining.match(/^([a-zA-Z][a-zA-Z0-9-_]*)/);
        if (match && !result.element && !result.id && !result.classes && !result.attributes && !result.pseudoClasses) {
          result.element = match[1];
          remaining = remaining.substring(match[0].length);
        } else {
          break;
        }
      }
    }
  }

  private static executeCSS(root: ICSElement, parsed: ParsedCSSSelector): ICSElement[] {
    // Phase 1: Find elements matching the first part of the selector.
    // If the selector part is empty (e.g., '> .foo'), the initial context is just the root.
    const isPartEmpty = !parsed.element && !parsed.id && !parsed.classes && !parsed.attributes && !parsed.pseudoClasses;
    const initialMatches = isPartEmpty ? [root] : this.findDescendantsAndSelf(root, parsed);

    // If there's no combinator, we're done.
    if (!parsed.combinator || !parsed.next) {
      return initialMatches;
    }

    // Phase 2: Apply combinator to find the next set of elements.
    const finalMatches = new Set<ICSElement>();
    for (const element of initialMatches) {
        switch (parsed.combinator) {
            case '>':
                const children = element.getAllElements() as ICSElement[];
                for (const child of children) {
                    if (this.matchesParsedSelector(child, parsed.next)) {
                        finalMatches.add(child);
                    }
                }
                break;
            case ' ':
                const descendants = this.findDescendantsAndSelf(element, parsed.next);
                // Exclude the element itself from its descendants
                descendants.forEach(d => {
                    if (d.id !== element.id) finalMatches.add(d);
                });
                break;
            case '+':
                const parentPlus = element.mainOwner;
                if (parentPlus) {
                    const siblings = parentPlus.getAllElements();
                    const elementIndex = siblings.findIndex(el => el.id === element.id);
                    if (elementIndex > -1 && elementIndex + 1 < siblings.length) {
                        const nextSibling = siblings[elementIndex + 1] as ICSElement;
                        if (this.matchesParsedSelector(nextSibling, parsed.next)) {
                            finalMatches.add(nextSibling);
                        }
                    }
                }
                break;
            case '~':
                const parentTilde = element.mainOwner;
                if (parentTilde) {
                    const siblings = parentTilde.getAllElements();
                    const elementIndex = siblings.findIndex(el => el.id === element.id);
                    if (elementIndex > -1) {
                        for (let i = elementIndex + 1; i < siblings.length; i++) {
                            const nextSibling = siblings[i] as ICSElement;
                            if (this.matchesParsedSelector(nextSibling, parsed.next)) {
                                finalMatches.add(nextSibling);
                            }
                        }
                    }
                }
                break;
        }
    }

    return Array.from(finalMatches);
  }

  // Helper to find all descendants (and self) that match a simple selector part
  private static findDescendantsAndSelf(root: ICSElement, parsed: ParsedCSSSelector): ICSElement[] {
      const results: ICSElement[] = [];
      
      // Проверяем сам корневой элемент
      if (this.matchesParsedSelector(root, parsed)) {
          results.push(root);
      }
      
      // Рекурсивно ищем в потомках
      const searchInChildren = (element: ICSElement) => {
        const children = element.getAllElements() as ICSElement[];
        for (const child of children) {
          if (this.matchesParsedSelector(child, parsed)) {
            results.push(child);
          }
          // Рекурсивно ищем в потомках
          searchInChildren(child);
        }
      };
      
      searchInChildren(root);
      return results;
  }

  private static findDescendantsByParsedSelector(root: ICSElement, parsed: ParsedCSSSelector): ICSElement[] {
    const results: ICSElement[] = [];
    
    // Рекурсивно ищем среди потомков, не включая сам root
    const searchInChildren = (element: ICSElement) => {
      const children = element.getAllElements() as ICSElement[];
      for (const child of children) {
        if (this.matchesParsedSelector(child, parsed)) {
          results.push(child);
        }
        // Рекурсивно ищем в потомках
        searchInChildren(child);
      }
    };
    
    searchInChildren(root);
    return results;
  }

  private static matchesParsedSelector(element: ICSElement, parsed: ParsedCSSSelector): boolean {
    // Проверяем имя элемента
    if (parsed.element && element.name !== parsed.element) {
      return false;
    }

    // Проверяем ID
    if (parsed.id && element.getData('id') !== parsed.id) {
      return false;
    }

    // Проверяем классы
    if (parsed.classes) {
      const elementClasses = element.getData('class');
      if (!elementClasses) return false;
      const classList = typeof elementClasses === 'string' ? elementClasses.split(' ') : [];
      for (const className of parsed.classes) {
        if (!classList.includes(className)) {
          return false;
        }
      }
    }

    // Проверяем атрибуты
    if (parsed.attributes) {
      for (const attr of parsed.attributes) {
        const value = element.getData(attr.name);
        

        
        // Если нет оператора, просто проверяем наличие атрибута
        if (!attr.operator) {
          if (value === undefined || value === null) {
            return false;
          }
          continue;
        }
        
        // Если есть оператор, проверяем значение
        if (!value) return false;
        
        if (attr.operator && attr.value) {
          switch (attr.operator) {
            case '=':
              if (value !== attr.value) return false;
              break;
            case '~=':
              if (!value.toString().split(' ').includes(attr.value)) return false;
              break;
            case '|=':
              if (!value.toString().startsWith(attr.value + '-') && value !== attr.value) return false;
              break;
            case '^=':
              if (!value.toString().startsWith(attr.value)) return false;
              break;
            case '$=':
              if (!value.toString().endsWith(attr.value)) return false;
              break;
            case '*=':
              if (!value.toString().includes(attr.value)) return false;
              break;
          }
        }
      }
    }

    // Проверяем псевдо-классы
    if (parsed.pseudoClasses) {
      for (const pseudo of parsed.pseudoClasses) {
        if (!this.matchesPseudoClass(element, pseudo)) {
          return false;
        }
      }
    }

    return true;
  }

  private static matchesPseudoClass(element: ICSElement, pseudo: { name: string; argument?: string }): boolean {
    switch (pseudo.name) {
      case 'first-child':
        return element.index === 0;
      case 'last-child':
        const parent = element.mainOwner;
        return parent ? element.index === parent.elementsCount() - 1 : true;
      case 'nth-child':
        if (pseudo.argument) {
          const n = parseInt(pseudo.argument);
          return element.index === n - 1; // CSS использует 1-based индексы
        }
        return false;
      case 'empty':
        return element.elementsCount() === 0;
      case 'not':
        // Реализация :not() селектора
        if (pseudo.argument) {
          const notParsed = this.parseCSS(pseudo.argument);
          return !this.matchesParsedSelector(element, notParsed);
        }
        return false;
      case 'has':
        // Реализация :has() селектора
        if (pseudo.argument) {
          const hasParsed = this.parseCSS(pseudo.argument);
          // Ищем только среди потомков, не включая сам элемент
          const descendants = this.findDescendantsByParsedSelector(element, hasParsed);
          return descendants.length > 0;
        }
        return false;
      default:
        return false;
    }
  }

  private static createXPathContext(root: ICSElement): any {
    // Этот метод больше не нужен, так как мы используем JSPath
    // который работает с JSON-объектами напрямую.
    return { root };
  }

  private static evaluateXPath(context: any, expression: string): ICSElement[] {
    const rootElement = context.root as CSElement;
    
    // 1. Создаем карту всех элементов для быстрого доступа по ID
    const elementMap = new Map<string, ICSElement>();
    const traverseAndMap = (element: ICSElement) => {
      elementMap.set(element.id, element);
      element.getAllElements().forEach(child => traverseAndMap(child as CSElement));
    };
    traverseAndMap(rootElement);

    // 2. Преобразуем структуру в JSON
    const jsonObject = rootElement.toJSPathObject();

    // 3. Применяем JSPath
    let results = JSPath.apply(expression, jsonObject);
    if (results === undefined) {
      results = [];
    } else if (!Array.isArray(results)) {
      results = [results];
    }

    // 4. Сопоставляем результаты с реальными элементами CSElement
    const finalElements: ICSElement[] = [];
    const seenIds = new Set<string>();

    for (const res of results) {
      if (res && typeof res === 'object' && res.___id) {
        const elementId = res.___id;
        if (!seenIds.has(elementId)) {
          const element = elementMap.get(elementId);
          if (element) {
            finalElements.push(element);
            seenIds.add(elementId);
          }
        }
      }
    }
    
    return finalElements;
  }

  private static traverseAndIndex(element: ICSElement, index: ElementIndex, depth: number): void {
    // Добавляем в общий индекс
    index.all.add(element);

    // Индексируем по имени
    if (element.name) {
      if (!index.byName.has(element.name)) {
        index.byName.set(element.name, new Set());
      }
      index.byName.get(element.name)!.add(element);
    }

    // Индексируем по ID
    const id = element.getData('id');
    if (id) {
      index.byId.set(id, element);
    }

    // Индексируем по классам
    const classes = element.getData('class');
    if (classes) {
      const classList = typeof classes === 'string' ? classes.split(' ') : [classes];
      for (const className of classList) {
        if (!index.byClass.has(className)) {
          index.byClass.set(className, new Set());
        }
        index.byClass.get(className)!.add(element);
      }
    }

    // Индексируем по атрибутам
    const data = element.data;
    for (const [key, value] of data) {
      if (!index.byAttribute.has(key)) {
        index.byAttribute.set(key, new Map());
      }
      const attrMap = index.byAttribute.get(key)!;
      if (!attrMap.has(value)) {
        attrMap.set(value, new Set());
      }
      attrMap.get(value)!.add(element);
    }

    // Индексируем по глубине
    if (!index.byDepth.has(depth)) {
      index.byDepth.set(depth, new Set());
    }
    index.byDepth.get(depth)!.add(element);

    // Рекурсивно обрабатываем дочерние элементы
    const children = element.getAllElements() as ICSElement[];
    for (const child of children) {
      this.traverseAndIndex(child, index, depth + 1);
    }
  }

  // ===== Methods from old QueryEngine =====

  /**
   * Выполняет поиск по объекту селектора
   */
  private static queryBySelector(root: ICSElement, selector: QuerySelector): ICSElement[] {
    const results: ICSElement[] = [];
    this.traverseAndMatchObject(root, selector, results);
    return results;
  }

  /**
   * Рекурсивно обходит дерево и собирает подходящие элементы для объектного селектора
   */
  private static traverseAndMatchObject(element: ICSElement, selector: QuerySelector, results: ICSElement[]): void {
    if (this.matchesObjectSelector(element, selector)) {
      results.push(element);
    }

    const children = element.getAllElements();
    for (const child of children) {
      this.traverseAndMatchObject(child as ICSElement, selector, results);
    }
  }

  /**
   * Проверяет, соответствует ли элемент объектному селектору
   */
  private static matchesObjectSelector(element: ICSElement, selector: QuerySelector): boolean {
    if (selector.name !== undefined && element.name !== selector.name) {
      return false;
    }

    if (selector.index !== undefined && element.index !== selector.index) {
      return false;
    }

    if (selector.hasData) {
      for (const key of selector.hasData) {
        if (!element.getData(key)) {
          return false;
        }
      }
    }

    if (selector.depth !== undefined) {
      const depth = this.getElementDepth(element);
      if (typeof selector.depth === 'number') {
        if (depth !== selector.depth) return false;
      } else {
        const { min, max } = selector.depth;
        if (min !== undefined && depth < min) return false;
        if (max !== undefined && depth > max) return false;
      }
    }

    if (selector.custom && !selector.custom(element)) {
      return false;
    }

    return true;
  }
  
  /**
   * Вычисляет глубину элемента в дереве
   */
  private static getElementDepth(element: ICSElement): number {
    let depth = 0;
    let current = element.mainOwner;
    while (current) {
      depth++;
      current = current.mainOwner;
    }
    return depth;
  }

  /**
   * Создает комплексный селектор для поиска элементов
   */
  static createSelector(): SelectorBuilder {
    return new SelectorBuilder();
  }

  // ===== End of methods from old QueryEngine =====
}

/**
 * Builder для создания сложных селекторов
 */
export class SelectorBuilder {
  private selector: QuerySelector = {};

  withName(name: string): this {
    this.selector.name = name;
    return this;
  }

  withIndex(index: number): this {
    this.selector.index = index;
    return this;
  }

  withData(...keys: string[]): this {
    this.selector.hasData = [...(this.selector.hasData || []), ...keys];
    return this;
  }

  withDepth(depth: number | { min?: number; max?: number }): this {
    this.selector.depth = depth;
    return this;
  }

  withCustom(predicate: (element: ICSElement) => boolean): this {
    const existing = this.selector.custom;
    if (existing) {
      this.selector.custom = (element) => existing(element) && predicate(element);
    } else {
      this.selector.custom = predicate;
    }
    return this;
  }

  build(): QuerySelector {
    return { ...this.selector };
  }
}

/**
 * Предопределенные селекторы
 */
export const CommonSelectors = {
  byName: (name: string): QuerySelector => ({ name }),
  byIndex: (index: number): QuerySelector => ({ index }),
  leaves: (): QuerySelector => ({
    custom: (element) => element.elementsCount() === 0
  }),
  roots: (): QuerySelector => ({
    custom: (element) => element.mainOwner === null
  }),
  withChildrenCount: (count: number): QuerySelector => ({
    custom: (element) => element.elementsCount() === count
  }),
  atDepth: (depth: number): QuerySelector => ({ depth }),
  withDataType: (key: string, type: string): QuerySelector => ({
    custom: (element) => typeof element.getData(key) === type
  })
}; 